import clsx from "clsx";
import PropTypes from "prop-types";
import {
  Checkbox as DSCheckbox,
  CheckboxLabel,
} from "@hireart/component-library";
import { forwardRef, useState } from "react";

/**
 * Adapter wrapping the design system Checkbox with SUI-compatible API.
 *
 * SUI signature:  onChange(event, { checked, value, name, ...rest })
 * DS  signature:  onChange(event)
 *
 * Mirrors SUI's controlled/uncontrolled toggle: when `checked` is omitted,
 * the adapter keeps internal state and toggles itself on click. Several
 * consumers (e.g. Login's "Remember me") rely on this by passing only
 * `value` + `onChange` and would otherwise render as permanently unchecked.
 *
 * `compact` collapses the design system wrapper for dense vertical
 * layouts (e.g. filter lists) where the default 48px row stride is
 * visually disruptive. Drops vertical margin and all padding but
 * preserves an 8px right margin so adjacent label text still has a
 * gap. The 24px input itself is unchanged.
 */
export const Checkbox = forwardRef(
  (
    {
      checked,
      defaultChecked,
      className,
      compact = false,
      disabled = false,
      id,
      label,
      name,
      onChange,
      value,
      ...rest
    },
    ref
  ) => {
    const isControlled = checked !== undefined;
    const [internalChecked, setInternalChecked] = useState(
      defaultChecked ?? false
    );
    const currentChecked = isControlled ? checked : internalChecked;

    const handleChange = (e) => {
      const nextChecked = e.target.checked;
      if (!isControlled) setInternalChecked(nextChecked);
      if (!onChange) return;
      onChange(e, {
        checked: nextChecked,
        value,
        name,
        id,
        ...rest,
      });
    };

    const checkbox = (
      <DSCheckbox
        ref={ref}
        className={clsx(compact && "!my-0 !ml-0 !mr-8 !p-0", className)}
        id={id}
        isChecked={currentChecked}
        isDisabled={disabled}
        name={name}
        onChange={handleChange}
        value={value}
      />
    );

    if (!label) return checkbox;

    const labelContent =
      typeof label === "object" && label.children ? label.children : label;
    const labelHtmlFor =
      typeof label === "object" && label.htmlFor ? label.htmlFor : id;

    return (
      <CheckboxLabel htmlFor={labelHtmlFor}>
        {checkbox}
        {labelContent}
      </CheckboxLabel>
    );
  }
);

Checkbox.displayName = "Checkbox";

Checkbox.propTypes = {
  checked: PropTypes.bool,
  defaultChecked: PropTypes.bool,
  className: PropTypes.string,
  compact: PropTypes.bool,
  disabled: PropTypes.bool,
  id: PropTypes.string,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node, PropTypes.object]),
  name: PropTypes.string,
  onChange: PropTypes.func,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};
